/**
 * GA4, behind one function so nothing else ever mentions gtag.
 *
 * The tag only arms on the deployed site: the dev server, `yarn preview` and
 * the CDP verification harness all run on localhost, and every one of them
 * would otherwise pour synthetic rooms into the property. An ad blocker eating
 * the loader leaves `dataLayer` accumulating quietly — pushes still cost
 * nothing, so `track` never needs to know whether anyone is listening.
 *
 * Nothing identifying leaves the page: no player names, no room codes, no peer
 * IDs. What is measured is which features get used — rooms, systems, games.
 */

const GA_ID = 'G-4DEEY73L3V';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const enabled =
  import.meta.env.PROD &&
  !['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);

export function initAnalytics(): void {
  if (!enabled || window.gtag) return;
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js distinguishes commands from events by the Arguments object itself,
  // so this must push `arguments`, not a copied array.
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID);
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.append(s);
}

/** Fire-and-forget. A no-op everywhere the tag never armed. */
export function track(event: string, params: Record<string, string | number | boolean> = {}): void {
  window.gtag?.('event', event, params);
}
